import { Stack, SxProps, Theme, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { OtherIcon, PlaneIcon, ShipIcon, TrainIcon, TruckIcon } from "../../../../../../assets/icons";
import { formatNumber } from "../../../../../../common/utils";
import {
	EmissionsHaulageTransportResultsDTO,
	EmissionsTransportResultsDatumNoIntensityDTO,
	ExtendedMeasurableValueDTO,
} from "../../../../../../datasources/api-client/data-contracts";
import { TransportTypesColors } from "../../../../../../theme/themeValues";
import { headerCellMeasure, MeasureTypes } from "../../../../utils";

// PIE CHART DATA
const typesData = (t: TFunction) =>
	({
		road: {
			label: t("transportType/road"),
			icon: <TruckIcon style={{ color: TransportTypesColors.LightRoad }} />,
		},
		train: {
			label: t("transportType/train"),
			icon: <TrainIcon style={{ color: TransportTypesColors.LightTrain }} />,
		},
		ship: {
			label: t("transportType/ship"),
			icon: <ShipIcon style={{ color: TransportTypesColors.LightShip }} />,
		},
		air: {
			label: t("transportType/air"),
			icon: <PlaneIcon style={{ color: TransportTypesColors.LightAir }} />,
		},
	}) as const;

type ModeKey = keyof ReturnType<typeof typesData>;

export type EmissionDataKey = keyof EmissionsTransportResultsDatumNoIntensityDTO;

export const getDataChart = (data: EmissionsHaulageTransportResultsDTO, selectMeasure: string, t: TFunction) => {
	// Get total value and avoid equal to 0
	const totalValue = data?.total?.[selectMeasure as EmissionDataKey]?.value;

	const metaData = typesData(t);

	return (
		Object.entries(data)
			.filter(([key]) => key !== "total")
			.map(([key, value]: [string, ExtendedMeasurableValueDTO]) => {
				const percent = totalValue ? (value[selectMeasure as EmissionDataKey].value / totalValue) * 100 : 0;
				const meta = metaData[key as ModeKey];

				return {
					id: key,
					percent: Number(percent.toFixed(2)),
					value: value[selectMeasure as EmissionDataKey].value,
					label: meta.label,
					icon: meta.icon,
				};
			}) || []
	);
};

const capitalize = (str: string) => {
	if (!str) return "";
	return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// returns the icon associated either to the type as TransportTypesKeys or as the transport type id (number)
export const getTransportTypeIcon = (type: string | number, sxContent?: SxProps<Theme>) => {
	switch (type) {
		case "road":
			return <TruckIcon sx={{ ...sxContent, color: TransportTypesColors.LightRoad }} />;
		case 1:
			return <TruckIcon sx={{ ...sxContent, color: TransportTypesColors.LightRoad }} />;
		case "train":
			return <TrainIcon sx={{ ...sxContent, color: TransportTypesColors.LightTrain }} />;
		case 2:
			return <TrainIcon sx={{ ...sxContent, color: TransportTypesColors.LightTrain }} />;
		case "ship":
			return <ShipIcon sx={{ ...sxContent, color: TransportTypesColors.LightShip }} />;
		case 3:
			return <ShipIcon sx={{ ...sxContent, color: TransportTypesColors.LightShip }} />;
		case "air":
			return <PlaneIcon sx={{ ...sxContent, color: TransportTypesColors.LightAir }} />;
		case 4:
			return <PlaneIcon sx={{ ...sxContent, color: TransportTypesColors.LightAir }} />;
		case "other":
			return <OtherIcon sx={{ ...sxContent, color: TransportTypesColors.LightOther }} />;
		default:
			return null;
	}
};

export const getRows = (data: EmissionsHaulageTransportResultsDTO, selectMeasure: string, t: TFunction) => {
	const totalWeight = data.total.weight.value;
	const totalEmissions = data?.total?.[selectMeasure as EmissionDataKey]?.value;

	return (
		data &&
		Object.entries(data)
			.filter(([type, value]) => type !== "total" && value != null)
			.map(([type, value]: [string, ExtendedMeasurableValueDTO], index) => ({
				id: index,
				type: t(`transportType/${type}`),
				iconType: type,
				weight: {
					value: value.weight.value,
					percent: totalWeight !== 0 ? (value.weight.value / totalWeight) * 100 : 0,
				},
				emissions: {
					value: value[selectMeasure as EmissionDataKey].value,
					percent:
						totalEmissions && totalEmissions !== 0
							? (value[selectMeasure as EmissionDataKey].value / totalEmissions) * 100
							: 0,
				},
			}))
	);
};

export const dataCol: GridColDef[] = [
	{
		field: "type",
		headerName: "type of transport",
		flex: 1,
		sortable: false,
		renderCell: (params) => (
			<Stack direction="row" spacing={1} alignItems="center" height={"100%"}>
				{getTransportTypeIcon(params.row.iconType)}
				<Typography variant="body1">{capitalize(params.value)}</Typography>
			</Stack>
		),
	},
	{
		field: "emissions",
		headerName: "emissions",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			return params ? (
				<Stack spacing={1} height={"100%"} direction={"row"} justifyContent={"flex-end"} alignItems={"center"}>
					<Typography variant="body1" fontWeight={700}>
						{formatNumber(params.value.value, "decimal", 2, 2)}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						({formatNumber(params.value.percent / 100, "percent", 2, 2)})
					</Typography>
				</Stack>
			) : (
				<>-</>
			);
		},
	},
	{
		field: "weight",
		headerName: "weight",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			return params ? (
				<Stack spacing={1} height={"100%"} direction={"row"} justifyContent={"flex-end"} alignItems={"center"}>
					<Typography variant="body1" fontWeight={700}>
						{formatNumber(params.value.value, "decimal", 2, 2)}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						({formatNumber(params.value.percent / 100, "percent", 2, 2)})
					</Typography>
				</Stack>
			) : (
				<>-</>
			);
		},
	},
];

export const getColumns = (
	dataCol: GridColDef[],
	dataSample: MeasureTypes,
	t: TFunction,
	overrideFields?: (old?: string) => string | undefined
) =>
	dataCol.map((col) => ({
		...col,
		headerName: t(col.headerName ?? "-"),
		renderHeader: () => (
			<Stack
				direction="row"
				sx={{
					fontWeight: "bold",
				}}
				spacing={1}
				alignItems={"baseline"}
			>
				<Typography variant="subtitle1">{t(col.headerName ?? "-")}</Typography>
				<Typography variant="body2" color="text.secondary">
					{headerCellMeasure(col, dataSample, overrideFields)}
				</Typography>
			</Stack>
		),
	}));
